import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, CheckCircle, FileText } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

interface PresentationViewerProps {
  section: any;
  onComplete: () => void;
  onClose: () => void;
}

export const PresentationViewer = ({ section, onComplete, onClose }: PresentationViewerProps) => {
  const { language } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [presentationUrl, setPresentationUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPresentation = async () => {
      if (section.presentation_file_path) {
        try {
          // Use createSignedUrl for private buckets (expires in 1 hour)
          const { data, error } = await supabase.storage
            .from('training-presentations')
            .createSignedUrl(section.presentation_file_path, 3600);
          
          if (error) throw error;
          setPresentationUrl(data.signedUrl);
        } catch (error) {
          console.error('Error loading presentation:', error);
        }
      }
      setIsLoading(false);
    };

    loadPresentation();
  }, [section.presentation_file_path]);

  const slides = [
    { title: language === 'en' ? section.title_en : section.title_el, content: "Slide 1" },
    { title: "Slide 2", content: "Slide 2" },
    { title: "Slide 3", content: "Slide 3" }
  ];

  const handleComplete = () => {
    setIsCompleted(true);
    onComplete();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            {language === 'en' ? section.title_en : section.title_el}
          </CardTitle>
          <Button variant="outline" onClick={onClose}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Back' : 'Πίσω'}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center mb-6">
            {isLoading ? (
              <div className="text-center p-8">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-muted-foreground">{language === 'en' ? 'Loading presentation...' : 'Φόρτωση παρουσίασης...'}</p>
              </div>
            ) : presentationUrl ? (
              <div className="w-full h-full">
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(presentationUrl)}`}
                  className="w-full h-full rounded-lg border-0"
                  title={language === 'en' ? section.title_en : section.title_el}
                />
              </div>
            ) : (
              <div className="text-center p-8">
                <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-4">{slides[currentSlide].title}</h3>
                <p className="text-muted-foreground">
                  {language === 'en' ? 'No presentation file available' : 'Δεν υπάρχει αρχείο παρουσίασης'}
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
              disabled={currentSlide === 0}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Previous' : 'Προηγούμενο'}
            </Button>

            <span className="text-sm text-muted-foreground">
              {currentSlide + 1} / {slides.length}
            </span>

            {currentSlide < slides.length - 1 ? (
              <Button
                onClick={() => setCurrentSlide(currentSlide + 1)}
              >
                {language === 'en' ? 'Next' : 'Επόμενο'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                disabled={isCompleted}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {language === 'en' ? 'Complete' : 'Ολοκλήρωση'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};